/**
 * Intelligence state management.
 *
 * Manages SOAP notes, transcript entries, alerts,
 * and summary data from Feed A, C, and E.
 * Uses Zustand with immer for immutable updates.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { TranscriptEntry, FeedCAlert } from '@assistmd/types';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface SoapNote {
  S: string;
  O: string;
  A: string;
  P: string;
  version: number;
}

export interface Alert {
  id: string;
  visitId?: string;
  severity: 'critical' | 'warning' | 'info';
  code: string;
  message: string;
  at: string;
  dismissed: boolean;
}

export interface IntelligenceState {
  /** Live transcript entries */
  transcriptEntries: TranscriptEntry[];
  /** Interim (non-final) transcript entry */
  interimEntry: TranscriptEntry | null;

  /** SOAP note */
  soap: SoapNote | null;
  /** Short summary */
  summary: string | null;
  /** Summary version */
  summaryVersion: number;

  /** Clinical alerts */
  alerts: Alert[];
  /** Unread alert count */
  unreadAlertCount: number;

  /** Intelligence processing status */
  processingStatus: 'idle' | 'processing' | 'error';
  processingError: string | null;
}

interface IntelligenceActions {
  // Transcript
  addTranscriptEntry: (entry: TranscriptEntry) => void;
  setInterimEntry: (entry: TranscriptEntry | null) => void;
  clearTranscript: () => void;

  // SOAP
  setSoap: (soap: SoapNote) => void;

  // Summary
  setSummary: (summary: string, version: number) => void;

  // Alerts
  addAlert: (alert: FeedCAlert['data']) => void;
  dismissAlert: (id: string) => void;
  clearAlerts: () => void;

  // Processing
  setProcessingStatus: (status: IntelligenceState['processingStatus'], error?: string) => void;

  // Reset
  clear: () => void;
}

type IntelligenceStore = IntelligenceState & IntelligenceActions;

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MAX_TRANSCRIPT_ENTRIES = 500;
const MAX_ALERTS = 100;

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

const initialState: IntelligenceState = {
  transcriptEntries: [],
  interimEntry: null,
  soap: null,
  summary: null,
  summaryVersion: 0,
  alerts: [],
  unreadAlertCount: 0,
  processingStatus: 'idle',
  processingError: null,
};

export const useIntelligenceStore = create<IntelligenceStore>()(
  immer((set) => ({
    ...initialState,

    // ── Transcript ──────────────────────────────────────────────────────

    addTranscriptEntry: (entry) => {
      set((state) => {
        const idx = state.transcriptEntries.findIndex((e) => e.id === entry.id);
        if (idx >= 0) {
          state.transcriptEntries[idx] = entry;
        } else {
          state.transcriptEntries.push(entry);
        }
        // Enforce max entries (FIFO)
        if (state.transcriptEntries.length > MAX_TRANSCRIPT_ENTRIES) {
          state.transcriptEntries = state.transcriptEntries.slice(-MAX_TRANSCRIPT_ENTRIES);
        }
      });
    },

    setInterimEntry: (entry) => {
      set((state) => {
        state.interimEntry = entry;
      });
    },

    clearTranscript: () => {
      set((state) => {
        state.transcriptEntries = [];
        state.interimEntry = null;
      });
    },

    // ── SOAP ────────────────────────────────────────────────────────────

    setSoap: (soap) => {
      set((state) => {
        state.soap = soap;
      });
    },

    // ── Summary ─────────────────────────────────────────────────────────

    setSummary: (summary, version) => {
      set((state) => {
        state.summary = summary;
        state.summaryVersion = version;
      });
    },

    // ── Alerts ──────────────────────────────────────────────────────────

    addAlert: (alertData) => {
      set((state) => {
        const newAlert: Alert = {
          ...alertData,
          dismissed: false,
        };
        state.alerts.unshift(newAlert);
        state.unreadAlertCount += 1;
        // Enforce max alerts
        if (state.alerts.length > MAX_ALERTS) {
          state.alerts = state.alerts.slice(0, MAX_ALERTS);
        }
      });
    },

    dismissAlert: (id) => {
      set((state) => {
        const alert = state.alerts.find((a) => a.id === id);
        if (alert && !alert.dismissed) {
          alert.dismissed = true;
          state.unreadAlertCount = Math.max(0, state.unreadAlertCount - 1);
        }
      });
    },

    clearAlerts: () => {
      set((state) => {
        state.alerts = [];
        state.unreadAlertCount = 0;
      });
    },

    // ── Processing ──────────────────────────────────────────────────────

    setProcessingStatus: (status, error) => {
      set((state) => {
        state.processingStatus = status;
        state.processingError = error ?? null;
      });
    },

    // ── Reset ───────────────────────────────────────────────────────────

    clear: () => {
      set(() => ({ ...initialState }));
    },
  })),
);
